import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { useLanguage } from '../language/LanguageContext';
import { SectionType } from '../types';

interface NavigationProps {
  activeSection: SectionType;
  isScrolled: boolean;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  handleNavClick: (section: SectionType) => void;
}

const Navigation: React.FC<NavigationProps> = ({
  activeSection,
  isScrolled,
  mobileMenuOpen,
  setMobileMenuOpen,
  handleNavClick
}) => {
  const { translations, language } = useLanguage();
  const { nav } = translations;

  const navSections: { id: SectionType; label: string }[] = [
    { id: 'about', label: nav.about },
    { id: 'experience', label: nav.experience },
    { id: 'skills', label: nav.skills },
    { id: 'education', label: nav.education },
    { id: 'contact', label: nav.contact }
  ];

  const listRef = useRef<HTMLUListElement>(null);
  const linkRefs = useRef<Partial<Record<SectionType, HTMLAnchorElement | null>>>({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Slide the underline between items instead of unmounting/remounting it.
  const measure = useCallback(() => {
    const el = linkRefs.current[activeSection];
    const list = listRef.current;
    if (!el || !list) return;

    const elRect = el.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    setIndicator({ left: elRect.left - listRect.left, width: elRect.width });
  }, [activeSection]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);

    // Labels are measured before the webfont swaps in, which would leave the
    // indicator sized to the fallback font. Re-measure once Inter is ready.
    let cancelled = false;
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (!cancelled) measure();
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
    };
    // language is a dependency because label widths change with translation
  }, [measure, language]);

  return (
    <nav
      aria-label="Sections"
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        isScrolled
          ? 'bg-dark-bg/80 backdrop-blur-xl border-b border-dark-border'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-5xl mx-auto px-5">
        <div className="flex justify-between items-center py-4 md:hidden">
          <a
            href="#about"
            className="font-extrabold text-dark-heading border border-dark-border rounded-lg px-2.5 py-0.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            RF
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-dark-muted hover:text-dark-heading focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg rounded-lg p-1 transition-colors duration-200"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        <div
          id="mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out-soft ${
            mobileMenuOpen
              ? 'opacity-100 translate-y-0 max-h-[32rem]'
              : 'opacity-0 -translate-y-2 max-h-0 pointer-events-none'
          }`}
        >
          <ul className="flex flex-col py-2">
            {navSections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={() => handleNavClick(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={`relative block py-3 px-4 lowercase rounded-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset ${
                      isActive
                        ? 'text-dark-heading font-medium bg-dark-surface'
                        : 'text-dark-muted hover:text-dark-heading'
                    }`}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-accent-gradient" />
                    )}
                    {section.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Desktop menu */}
        <ul ref={listRef} className="hidden md:flex relative">
          {navSections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <li key={section.id}>
                <a
                  ref={(el) => {
                    linkRefs.current[section.id] = el;
                  }}
                  href={`#${section.id}`}
                  onClick={() => handleNavClick(section.id)}
                  aria-current={isActive ? 'true' : undefined}
                  className={`relative block py-5 px-6 lowercase transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset rounded-lg ${
                    isActive ? 'text-dark-heading font-medium' : 'text-dark-muted hover:text-dark-heading'
                  }`}
                >
                  {section.label}
                </a>
              </li>
            );
          })}

          {/* Sliding active indicator */}
          <span
            aria-hidden="true"
            className="absolute bottom-0 h-0.5 rounded-full bg-accent-gradient transition-all duration-300 ease-out-soft"
            style={{
              left: `${indicator.left + 24}px`,
              width: `${Math.max(indicator.width - 48, 0)}px`,
              opacity: indicator.width ? 1 : 0
            }}
          />
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
